import * as React from 'react';
import { SlidersHorizontal, X } from 'lucide-react';
import { DMButton, DMDiv, DMText } from './Primitives';

export type ManagementFilterOption<TValue extends string> = {
  label: string;
  value: TValue;
};

type ManagementFilterBarProps<TValue extends string> = {
  options: ManagementFilterOption<TValue>[];
  // 1. UPDATE: Accept BOTH a single string or an array of strings
  selectedValue: TValue | TValue[];
  allValue: TValue;
  onChange: (value: TValue) => void;
  className?: string;
};

export function ManagementFilterBar<TValue extends string>({
  options,
  selectedValue,
  allValue,
  onChange,
  className = '',
}: ManagementFilterBarProps<TValue>) {
  const filterRef = React.useRef<HTMLDivElement>(null);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = React.useState(false);

  // 2. Normalize the prop: If it's a single string, wrap it in an array so .includes() always works
  const selectedValuesArray = Array.isArray(selectedValue)
    ? selectedValue
    : [selectedValue];

  // Find all active options excluding the "all" option
  const activeOptions = options.filter(
    option =>
      option.value !== allValue && selectedValuesArray.includes(option.value)
  );

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setIsFilterMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleChange = (value: TValue) => {
    onChange(value);
    setIsFilterMenuOpen(false); // Closes menu automatically on select
  };

  return (
    <DMDiv
      className={`relative flex shrink-0 items-center gap-[0.7vw] ${className}`}
      ref={filterRef}
    >
      <DMButton
        type="button"
        onClick={() => setIsFilterMenuOpen(current => !current)}
        className="flex h-[4.5vh] items-center gap-[0.4vw] rounded-full border border-gray-300 bg-white px-[1vw] text-[1.65vh] font-medium text-gray-600 shadow-sm hover:bg-gray-50"
      >
        <SlidersHorizontal className="h-[2vh] w-[2vh]" strokeWidth={1.9} />
        <DMText as="span">Filters</DMText>
        {activeOptions.length > 0 && (
          <DMText
            as="span"
            className="ml-[0.5vw] flex h-[2.5vh] w-[2.5vh] items-center justify-center rounded-full bg-primary-10 text-[1.4vh] font-bold text-primary-main"
          >
            {activeOptions.length}
          </DMText>
        )}
      </DMButton>

      {isFilterMenuOpen && (
        <DMDiv className="absolute left-0 top-full z-30 mt-2 w-48 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl">
          {options.map(option => {
            // Use our normalized array to check active status
            const isActive = selectedValuesArray.includes(option.value);

            return (
              <DMButton
                key={option.value}
                type="button"
                onClick={() => handleChange(option.value)}
                className={`flex w-full items-center px-4 py-3 text-sm transition-colors hover:bg-gray-50 ${
                  isActive
                    ? 'bg-[#E1DCFD] font-bold text-[#5E4DE0]'
                    : 'text-gray-700'
                }`}
              >
                {option.label}
              </DMButton>
            );
          })}
        </DMDiv>
      )}

      {activeOptions.map(option => (
        <DMText
          as="span"
          key={option.value}
          className="flex h-[4.5vh] items-center gap-[0.35vw] rounded-full bg-primary-main px-[1vw] text-[1.55vh] font-semibold text-white"
        >
          {option.label}
          <DMButton
            type="button"
            onClick={e => {
              e.stopPropagation();
              // 3. Smart 'X' button logic:
              // If used on a multi-select page, pass the clicked value to toggle it off.
              // If used on a single-select page, pass "allValue" to reset the filter.
              handleChange(
                Array.isArray(selectedValue) ? option.value : allValue
              );
            }}
            className="rounded-full border border-white/80 hover:bg-white/20"
          >
            <X className="h-[1.7vh] w-[1.7vh]" strokeWidth={2.2} />
          </DMButton>
        </DMText>
      ))}
    </DMDiv>
  );
}
